// Helpers for errors thrown by RestApiClient#checkStatus.
//
// Error shape (see utils/RestApiClient.js):
//   error.message  = "<status> <statusText>"
//   error.response = the fetch Response (has .status)
//   error.body     = parsed JSON body, if the server returned JSON
//   error.durationMs = how long the request took before it failed
//
// The server uses a consistent shape for 4xx validation errors:
//   { error: "some_code", message: "...", details: { field: "..." } }

type ApiErrorBody = {
  error?: string
  message?: string
  details?: {
    field?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

type ApiError = Error & {
  response?: { status: number; statusText?: string }
  body?: ApiErrorBody
  durationMs?: number
}

const isApiError = (error: unknown): error is ApiError => {
  return (
    error instanceof Error &&
    typeof (error as ApiError).response === "object" &&
    (error as ApiError).response !== null
  )
}

export const getApiErrorStatus = (error: unknown): number | null => {
  if (!isApiError(error)) return null
  return error.response?.status ?? null
}

export const isApiClientError = (error: unknown): boolean => {
  const status = getApiErrorStatus(error)
  return typeof status === "number" && status >= 400 && status < 500
}

export const getApiErrorBody = (error: unknown): ApiErrorBody | null => {
  return isApiError(error) ? (error.body ?? null) : null
}

export const getApiErrorMessage = (error: unknown): string | null => {
  const message = getApiErrorBody(error)?.message
  return typeof message === "string" && message.length > 0 ? message : null
}

export const getApiErrorField = (error: unknown): string | null => {
  const field = getApiErrorBody(error)?.details?.field
  return typeof field === "string" && field.length > 0 ? field : null
}

export const getApiErrorCode = (error: unknown): string | null => {
  const code = getApiErrorBody(error)?.error
  return typeof code === "string" && code.length > 0 ? code : null
}

// A failure that ran this long wasn't a fast error, it was a request that hung
// until something gave up on it. Deliberately well above how long a legitimate
// query over bulk data (logs, traces) can take, so only timeouts land here.
export const SLOW_FAILURE_MS = 20000

export const getApiErrorDuration = (error: unknown): number | null => {
  if (!(error instanceof Error)) {
    return null
  }

  const duration = (error as ApiError).durationMs
  return typeof duration === "number" ? duration : null
}

// React Query retry callback. It skips the retries that can't help:
//
//   - 4xx responses won't start succeeding on their own.
//   - Failures that took SLOW_FAILURE_MS or longer have timed out rather than
//     errored. Retrying a 30s timeout three more times piles four slow requests
//     onto a system that is already in trouble.
//
// Server errors (5xx, network) that came back in reasonable time still retry, as
// does an error we couldn't time, which is judged on its status alone.
export const retryOnlyServerErrors =
  (maxAttempts = 3, maxDurationMs = SLOW_FAILURE_MS) =>
  (failureCount: number, error: unknown) => {
    if (isApiClientError(error)) {
      return false
    }

    const duration = getApiErrorDuration(error)
    if (duration !== null && duration >= maxDurationMs) {
      return false
    }

    return failureCount < maxAttempts
  }

export type { ApiError, ApiErrorBody }
